import { z } from "zod";

/** Where a merch order is posted: the name and address the buyer gave the
 *  vendor's checkout. Personal data, served only on the order's own read
 *  to its signed-in holder. */
export const merchDeliveryAddressSchema = z.object({
  name: z.string(),
  line1: z.string(),
  city: z.string(),
  postcode: z.string(),
  /** ISO 3166 code ("GB"). */
  country: z.string(),
});
export type MerchDeliveryAddress = z.infer<typeof merchDeliveryAddressSchema>;

/**
 * One merch order of the signed-in holder's (a purchase bought on their
 * behalf from the product's store, which posts it to them), as the app's
 * wallet and order screen and Link in Bio's merch page show it.
 */
export const merchOrderViewSchema = z.object({
  /** The purchase: its buy-on-behalf attempt. */
  id: z.string().min(1),
  /** The store as the order's screen names it: "Music Glue" ("Music Glue
   *  posts it to you") and what its reference is called ("Music Glue
   *  order ID"). */
  seller: z.object({
    name: z.string().min(1),
    referenceLabel: z.string().min(1),
  }),
  /** When it was ordered, ISO 8601. */
  orderedAt: z.string(),
  /** The store's own reference for it (Music Glue's order ID); null while
   *  it is not known. */
  orderReference: z.string().nullable(),
  /** What its variants are chosen by ("Size"); null when only the
   *  quantity is chosen. */
  optionLabel: z.string().nullable(),
  /** What was bought: each option chosen ("L"; null for a product with
   *  nothing to choose) and how many of it. */
  items: z
    .array(
      z.object({
        option: z.string().nullable(),
        quantity: z.number().int().positive(),
      }),
    )
    .min(1),
  /** What the order charged, delivery included; null when not recorded. */
  total: z
    .object({
      amount: z.number().nonnegative(),
      currency: z.string().min(1),
    })
    .nullable(),
  /** When the store posted it (its dispatch email arrived), ISO 8601, and
   *  who sent it in the store's words ("Remedy Fulfilment"); null while it
   *  has not shipped. Absent from servers before it existed. */
  shipped: z
    .object({ at: z.string(), by: z.string().nullable() })
    .nullable()
    .optional(),
  /** A digital download's delivery, in place of `shipped`: when the
   *  store's email delivering it was sent on to the buyer (null while it
   *  has not arrived), and the download's own link read from it, only on
   *  the order's own read to its holder (null on a list, and when the email
   *  gave none: the buyer has the email). Absent for posted merch, and from
   *  servers before it existed. */
  download: z
    .object({ emailedAt: z.string().nullable(), url: z.url().nullable() })
    .optional(),
  /** Only on the order's own read (never on a list): where it is posted. */
  deliveryAddress: merchDeliveryAddressSchema.nullable().optional(),
});
export type MerchOrderView = z.infer<typeof merchOrderViewSchema>;

/** The signed-in viewer's orders of one merch product on an artist's Link
 *  in Bio page, newest first, each with the address it is posted to. Empty
 *  when they hold none. */
export const linkPageMerchOrdersSchema = z.object({
  orders: z.array(merchOrderViewSchema),
});
export type LinkPageMerchOrders = z.infer<typeof linkPageMerchOrdersSchema>;
